import * as RDialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import type { ReactNode } from 'react';

export interface ItemPaleta {
  id: string;
  tipo: 'modulo' | 'menu' | 'rotina' | 'cadastro' | 'registro' | 'acao';
  titulo: string;
  codigo?: string;
  trilha?: string;
  icone?: ReactNode;
  secao: string;
}

export interface PaletaProps {
  aberta: boolean;
  aoFechar: () => void;
  busca: string;
  aoBuscar: (texto: string) => void;
  /** Itens já filtrados e ordenados por quem chama (o ranking é da aplicação, 07 §2.5). */
  itens: ItemPaleta[];
  aoEscolher: (item: ItemPaleta, e: { novaAba: boolean; favoritar: boolean }) => void;
  vazio?: string;
}

/** Paleta Ctrl+K: combobox + listbox (cmdk), folha centralizada a 14vh, 640px (07 §2.5). */
export function Paleta({ aberta, aoFechar, busca, aoBuscar, itens, aoEscolher, vazio = 'Nada encontrado' }: PaletaProps) {
  const secoes = [...new Set(itens.map((i) => i.secao))];
  return (
    <RDialog.Root open={aberta} onOpenChange={(a) => !a && aoFechar()}>
      <RDialog.Portal>
        <RDialog.Overlay className="sobreposicao" />
        <RDialog.Content className="dialogo folha paleta" aria-describedby={undefined} data-testid="paleta">
          <RDialog.Title className="sr-only">Buscar rotina, registro ou ação</RDialog.Title>
          <Command shouldFilter={false} loop label="Buscar rotina, registro ou ação"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.altKey)) {
                const sel = document.querySelector<HTMLElement>('.paleta [cmdk-item][data-selected="true"]');
                const item = itens.find((i) => i.id === sel?.dataset.id);
                if (item) {
                  e.preventDefault();
                  aoEscolher(item, { novaAba: e.ctrlKey, favoritar: e.altKey });
                }
              }
            }}>
            <div style={{ position: 'relative' }}>
              <Search className="paleta-busca-icone" size={18} strokeWidth={1.75} aria-hidden="true" />
              <Command.Input value={busca} onValueChange={aoBuscar} placeholder="Buscar rotina, registro ou ação…  ( > ações · / rotinas )" autoFocus />
            </div>
            <Command.List>
              <Command.Empty>{vazio}</Command.Empty>
              {secoes.map((s) => (
                <Command.Group key={s} heading={s}>
                  {itens.filter((i) => i.secao === s).map((i) => (
                    <Command.Item key={i.id} value={i.id} data-id={i.id} onSelect={() => aoEscolher(i, { novaAba: false, favoritar: false })}>
                      <span className="paleta-icone" aria-hidden="true">{i.icone}</span>
                      <span>
                        {i.titulo}
                        {i.trilha && <span className="paleta-trilha">{i.trilha}</span>}
                      </span>
                      {i.codigo && <span className="mono texto-3">{i.codigo}</span>}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
            <div className="paleta-rodape" aria-hidden="true">
              <span><kbd className="atalho">↑↓</kbd> navegar</span>
              <span><kbd className="atalho">Enter</kbd> abrir</span>
              <span><kbd className="atalho">Alt+Enter</kbd> favoritar</span>
              <span><kbd className="atalho">Esc</kbd> fechar</span>
            </div>
            <div className="sr-only" aria-live="polite">{itens.length} resultados</div>
          </Command>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
